// Stammdaten, Konditionen, Bank & Steuer, Ansprechpartner und Löschprüfung (Handoff Dienstleister §9, E5). Reine Funktionen.

import { emailGueltig } from '../immobilien/portal';
import { ibanGueltig, ibanNorm, zahl } from './neuerDienstleister';
import { istOffeneRechnung, zuordnungLaeuft } from './liste';
import { istOffeneStufe, ticketStufe } from './detail';

const t = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());
const leer = (s: string) => (s.trim() ? s.trim() : null);
const nz = (s: string) => { const z = zahl(s); return z === null || Number.isNaN(z) ? null : z; };
const zahlFehler = (s: string) => { const z = zahl(s); return z !== null && (Number.isNaN(z) || z < 0); };

// ── Stammdaten ─────────────────────────────────────────────────────────────────

export interface StammEntwurf {
  name: string; ansprechpartner: string; email: string; telefon: string; notdienstTelefon: string; website: string;
  strasse: string; hausnummer: string; plz: string; stadt: string; notizen: string;
}

export function stammAus(d: Record<string, unknown>): StammEntwurf {
  return {
    name: t(d.name), ansprechpartner: t(d.ansprechpartner), email: t(d.email), telefon: t(d.telefon), notdienstTelefon: t(d.notdienst_telefon),
    website: t(d.website), strasse: t(d.strasse), hausnummer: t(d.hausnummer), plz: t(d.plz), stadt: t(d.stadt), notizen: t(d.notizen),
  };
}

export function stammFehler(e: StammEntwurf): Partial<Record<keyof StammEntwurf, string>> {
  const f: Partial<Record<keyof StammEntwurf, string>> = {};
  if (!e.name.trim()) f.name = 'Name ist Pflicht';
  if (e.email.trim() && !emailGueltig(e.email)) f.email = 'E-Mail prüfen';
  if (e.plz.trim() && !/^\d{5}$/.test(e.plz.trim())) f.plz = '5 Ziffern';
  return f;
}

export const geaendert = <T extends object>(a: T, b: T): boolean => (Object.keys(a) as (keyof T)[]).some(k => String(a[k]).trim() !== String(b[k]).trim());

/** Spalten am Dienstleister – E-Mail und Telefon laufen über die Kontaktkanäle (speichereKanaele spiegelt sie). */
export function stammPayload(e: StammEntwurf): Record<string, string | null> {
  return {
    name: e.name.trim(), ansprechpartner: leer(e.ansprechpartner), notdienst_telefon: leer(e.notdienstTelefon), website: leer(e.website),
    strasse: leer(e.strasse), hausnummer: leer(e.hausnummer), plz: leer(e.plz), stadt: leer(e.stadt), notizen: leer(e.notizen),
  };
}

// ── Konditionen ────────────────────────────────────────────────────────────────

export interface KondEntwurf {
  stundensatz: string; anfahrt: string; notdienstzuschlag: string; zahlungsziel: string; skontoProzent: string; skontoTage: string;
  freigabegrenze: number; modus: 'direkt' | 'angebot'; notfall: boolean; bevorzugt: boolean; vertretung: boolean;
}

const zt = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(v).replace('.', ','));

export function kondAus(d: Record<string, unknown>): KondEntwurf {
  return {
    stundensatz: zt(d.stundensatz), anfahrt: zt(d.anfahrt), notdienstzuschlag: zt(d.notdienstzuschlag_prozent), zahlungsziel: zt(d.zahlungsziel_tage),
    skontoProzent: zt(d.skonto_prozent), skontoTage: zt(d.skonto_tage), freigabegrenze: Number(d.freigabegrenze ?? 500) || 0,
    modus: d.beauftragung_modus === 'direkt' ? 'direkt' : 'angebot', notfall: d.ist_notfallkontakt === true, bevorzugt: d.ist_bevorzugt === true, vertretung: d.vertretung === true,
  };
}

export function kondFehler(e: KondEntwurf): Partial<Record<keyof KondEntwurf, string>> {
  const f: Partial<Record<keyof KondEntwurf, string>> = {};
  for (const k of ['stundensatz', 'anfahrt', 'notdienstzuschlag', 'zahlungsziel', 'skontoTage'] as const) if (zahlFehler(e[k])) f[k] = 'Zahl ≥ 0';
  const sk = zahl(e.skontoProzent);
  if (sk !== null && (Number.isNaN(sk) || sk < 0 || sk >= 100)) f.skontoProzent = '0 – 99 %';
  else if (sk !== null && sk > 0 && !e.skontoTage.trim()) f.skontoTage = 'Tage angeben';
  return f;
}

export function kondPayload(e: KondEntwurf): Record<string, string | number | boolean | null> {
  const ganz = (s: string) => { const z = nz(s); return z === null ? null : Math.round(z); };
  return {
    stundensatz: nz(e.stundensatz), anfahrt: nz(e.anfahrt), notdienstzuschlag_prozent: nz(e.notdienstzuschlag), zahlungsziel_tage: ganz(e.zahlungsziel),
    skonto_prozent: nz(e.skontoProzent), skonto_tage: ganz(e.skontoTage), freigabegrenze: e.freigabegrenze, beauftragung_modus: e.modus,
    ist_notfallkontakt: e.notfall, ist_bevorzugt: e.bevorzugt, vertretung: e.bevorzugt && e.vertretung,
  };
}

// ── Bank & Steuer ──────────────────────────────────────────────────────────────

export interface BankEntwurf { iban: string; kontoinhaber: string; bic: string; ustId: string; steuerNr: string; kleinunternehmer: boolean; reverseCharge: boolean }

export function bankAus(d: Record<string, unknown>): BankEntwurf {
  return {
    iban: t(d.iban).replace(/(.{4})/g, '$1 ').trim(), kontoinhaber: t(d.kontoinhaber), bic: t(d.bic), ustId: t(d.ust_id), steuerNr: t(d.steuer_nr),
    kleinunternehmer: d.kleinunternehmer === true, reverseCharge: d.reverse_charge_13b === true,
  };
}

export function bankFehler(e: BankEntwurf): Partial<Record<keyof BankEntwurf, string>> {
  const f: Partial<Record<keyof BankEntwurf, string>> = {};
  if (e.iban.trim() && !ibanGueltig(e.iban)) f.iban = 'IBAN prüfen';
  if (e.bic.trim() && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/i.test(e.bic.trim())) f.bic = 'BIC hat 8 oder 11 Zeichen';
  if (e.ustId.trim() && !/^[A-Z]{2}[A-Z0-9]{2,13}$/i.test(e.ustId.replace(/\s/g, ''))) f.ustId = 'z. B. DE123456789';
  return f;
}

export function bankPayload(e: BankEntwurf): Record<string, string | boolean | null> {
  return {
    iban: e.iban.trim() ? ibanNorm(e.iban) : null, kontoinhaber: leer(e.kontoinhaber), bic: e.bic.trim() ? e.bic.trim().toUpperCase() : null,
    ust_id: e.ustId.trim() ? e.ustId.replace(/\s/g, '').toUpperCase() : null, steuer_nr: leer(e.steuerNr),
    kleinunternehmer: e.kleinunternehmer, reverse_charge_13b: e.reverseCharge,
  };
}

// ── Ansprechpartner ────────────────────────────────────────────────────────────

export type KontaktRolle = 'monteur' | 'auftragsannahme' | 'bereitschaft' | 'rechnungen' | 'gf';
export const KONTAKT_ROLLEN: readonly { key: KontaktRolle; label: string }[] = [
  { key: 'monteur', label: 'Monteur / Techniker' }, { key: 'auftragsannahme', label: 'Auftragsannahme' }, { key: 'bereitschaft', label: '24/7 Bereitschaft' },
  { key: 'rechnungen', label: 'Rechnungen' }, { key: 'gf', label: 'Geschäftsführer' },
];
export const rollenLabel = (r: string) => KONTAKT_ROLLEN.find(x => x.key === r)?.label ?? r;

export function kontaktFehler(e: { name: string; email: string }): string | null {
  if (!e.name.trim()) return 'Name fehlt';
  if (e.email.trim() && !emailGueltig(e.email)) return 'E-Mail prüfen';
  return null;
}

// ── Gewerke (Mengen-Differenz, nie „alles löschen, neu anlegen“) ───────────────

export function kategorieDifferenz(alt: readonly string[], neu: readonly string[]): { hinzu: string[]; weg: string[] } {
  return { hinzu: neu.filter(x => !alt.includes(x)), weg: alt.filter(x => !neu.includes(x)) };
}

// ── Löschprüfung ───────────────────────────────────────────────────────────────

export interface Verknuepfung { art: 'objekt' | 'ticket' | 'rechnung'; titel: string; detail: string }

/** Blockiert bei laufender Objekt-Zuordnung, offenem Ticket oder offener Rechnung (README §9). */
export function loeschPruefung(p: {
  zuordnungen: readonly { objekt_id: string; aktiv: boolean | null; bis: string | null; vertragsart: string | null }[];
  objektName: (id: string) => string;
  tickets: readonly { ticket_nummer: string | null; titel: string | null; status: string | null }[];
  rechnungen: readonly { rechnungsnummer: string | null; brutto: number | null; status: string | null }[];
  heute: string;
}): Verknuepfung[] {
  return [
    ...p.zuordnungen.filter(o => zuordnungLaeuft(o, p.heute)).map(o => ({ art: 'objekt' as const, titel: p.objektName(o.objekt_id), detail: `Objekt-Zuordnung${o.vertragsart ? ` · ${o.vertragsart}` : ''}` })),
    ...p.tickets.filter(x => istOffeneStufe(ticketStufe(x.status))).map(x => ({ art: 'ticket' as const, titel: [x.ticket_nummer, x.titel].filter(Boolean).join(' · ') || 'Ticket', detail: 'Offenes Ticket' })),
    ...p.rechnungen.filter(r => istOffeneRechnung(r.status)).map(r => ({
      art: 'rechnung' as const, titel: r.rechnungsnummer || 'Rechnung', detail: `Offene Rechnung${r.brutto !== null ? ` · ${r.brutto.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}` : ''}`,
    })),
  ];
}

export function loeschText(name: string, n: number): string {
  return `${name} hat noch ${n} ${n === 1 ? 'Verknüpfung' : 'Verknüpfungen'}. Erst Zuordnungen entfernen und Tickets/Rechnungen abschließen – oder auf inaktiv setzen, dann bleibt die Historie erhalten.`;
}
